import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { 
  Youtube, FileText, Search, Share2, Palette, TrendingUp, MapPin, 
  ShoppingCart, ArrowRight, CheckCircle, Lightbulb, Sparkles,
  ChevronRight, Star, Zap, Users
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { USE_CASES, getToolsForUseCase, ALL_TOOLS } from "@/data/toolsData";

// Icon mapping
const iconMap = {
  Youtube, FileText, Search, Share2, Palette, TrendingUp, MapPin, ShoppingCart, Users
};

// Use Cases Overview Page
export default function UseCasesPage() {
  const { useCaseId } = useParams();
  const navigate = useNavigate();

  // If specific use case is selected, show detail page
  if (useCaseId) {
    return <UseCaseDetail useCaseId={useCaseId} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <Header />
      
      <main className="pt-20 pb-16">
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
              <Sparkles className="w-3 h-3 mr-1" />
              Smart Tool Discovery
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Find Tools by Your Goal
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose what you want to achieve, and we'll show you the perfect toolkit. 
              No more searching - just pick your goal and start growing.
            </p>
          </div>

          {/* Use Cases Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="use-cases-grid">
            {USE_CASES.map((useCase) => {
              const Icon = iconMap[useCase.icon] || Zap;
              const tools = getToolsForUseCase(useCase.id);
              
              return (
                <Card 
                  key={useCase.id}
                  className="group cursor-pointer hover:shadow-xl transition-all duration-300 border-0 overflow-hidden"
                  onClick={() => navigate(`/use-cases/${useCase.id}`)}
                  data-testid={`use-case-${useCase.id}`}
                >
                  <div className={`h-2 bg-gradient-to-r ${useCase.gradient}`} />
                  <CardHeader className="pb-2">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${useCase.gradient} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-lg group-hover:text-purple-600 transition-colors">
                      {useCase.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {useCase.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary" className="text-xs">
                        {tools.length} Tools
                      </Badge>
                      <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-purple-500 group-hover:translate-x-1 transition-all" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Popular Tools Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Most Popular Tools</h2>
            <p className="text-gray-600">Used by thousands of marketers daily</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {ALL_TOOLS.filter(t => t.isFeatured).slice(0, 6).map(tool => (
              <Link
                key={tool.id}
                to={`${tool.path}?tool=${tool.id}`}
                className="p-4 bg-white rounded-xl border hover:shadow-lg hover:border-purple-200 transition-all text-center group"
                data-testid={`popular-tool-${tool.id}`}
              >
                <div className="w-10 h-10 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg flex items-center justify-center mx-auto mb-2 group-hover:from-purple-200 group-hover:to-pink-200 transition-colors">
                  <Star className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-sm font-medium text-gray-900 group-hover:text-purple-600 transition-colors line-clamp-1">
                  {tool.name}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

// Use Case Detail Page
function UseCaseDetail({ useCaseId }) {
  const navigate = useNavigate();
  const useCase = USE_CASES.find(uc => uc.id === useCaseId);
  const tools = getToolsForUseCase(useCaseId);
  const Icon = iconMap[useCase?.icon] || Zap;

  if (!useCase) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Use Case Not Found</h2>
          <Button onClick={() => navigate('/use-cases')}>Back to Use Cases</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <Header />
      
      <main className="pt-20 pb-16">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/use-cases" className="hover:text-purple-600">Use Cases</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 font-medium">{useCase.name}</span>
          </div>
        </div>

        {/* Hero Section */}
        <div className={`bg-gradient-to-r ${useCase.gradient} py-16`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white" data-testid="use-case-title">
                  {useCase.name}
                </h1>
                <p className="text-white/90 mt-1">{useCase.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-6">
              <Badge className="bg-white/20 text-white border-0 backdrop-blur">
                {tools.length} Tools Included
              </Badge>
              <Badge className="bg-white/20 text-white border-0 backdrop-blur">
                <Zap className="w-3 h-3 mr-1" />
                Quick Start Guide
              </Badge>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Tools List */}
            <div className="lg:col-span-2">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Tools in This Workflow
              </h2>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="use-case-tools">
                {tools.map((tool, index) => (
                  <Link
                    key={tool.id}
                    to={`${tool.path}?tool=${tool.id}`}
                    className="flex items-start gap-4 p-4 bg-white rounded-xl border hover:shadow-lg hover:border-purple-200 transition-all group"
                    data-testid={`use-case-tool-${tool.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${useCase.gradient} flex items-center justify-center text-white font-bold shrink-0`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                        {tool.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {tool.description}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        {tool.isPremium && (
                          <Badge variant="outline" className="text-xs border-amber-300 text-amber-600">
                            Premium
                          </Badge>
                        )}
                        {tool.isNew && (
                          <Badge className="text-xs bg-green-100 text-green-700 border-0">
                            New
                          </Badge>
                        )}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-purple-500 group-hover:translate-x-1 transition-all shrink-0 mt-1" />
                  </Link>
                ))}
              </div>
            </div>

            {/* Tips Sidebar */}
            <div className="space-y-6">
              <Card className="border-0 shadow-lg">
                <CardHeader className={`bg-gradient-to-r ${useCase.gradient} text-white rounded-t-lg`}>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Lightbulb className="w-5 h-5" />
                    Pro Tips
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <ul className="space-y-3">
                    {useCase.tips.map((tip, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className={`w-5 h-5 shrink-0 mt-0.5 text-${useCase.color}-500`} />
                        <span className="text-sm text-gray-700">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Quick Start */}
              <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-pink-50">
                <CardContent className="pt-6">
                  <h3 className="font-bold text-gray-900 mb-3">Quick Start</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Begin with the first tool and work your way down. Each tool builds on the previous one.
                  </p>
                  <Button 
                    className={`w-full bg-gradient-to-r ${useCase.gradient} text-white hover:opacity-90`}
                    onClick={() => tools[0] && navigate(`${tools[0].path}?tool=${tools[0].id}`)}
                    data-testid="start-workflow-button"
                  >
                    Start with {tools[0]?.name}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>

              {/* Other Use Cases */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">Other Workflows</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {USE_CASES.filter(uc => uc.id !== useCaseId).slice(0, 4).map(uc => {
                      const UCIcon = iconMap[uc.icon] || Zap;
                      return (
                        <Link
                          key={uc.id}
                          to={`/use-cases/${uc.id}`}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                        >
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${uc.gradient} flex items-center justify-center`}>
                            <UCIcon className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-sm font-medium text-gray-700 group-hover:text-purple-600">
                            {uc.name}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
